import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Eye, Globe } from "lucide-react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { fadeUp } from "../animations/variants";
import { publicClient } from "../config/axiosConfig";
import { footerText } from "../i18n/footer";
import { translations } from "../i18n/translation";

const Footer = () => {
  const [gaStats, setGaStats] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const lang = useSelector((state) => state.language.lang);

  const t = footerText[lang];
  const navT = translations[lang];

  const footerNavItems = [
    { key: "home", target: "#hero" },
    { key: "about", target: "#intro" },
    { key: "approach", target: "#about" },
    { key: "services", target: "#services" },

    { key: "download", path: "/free-download" },
    { key: "blog", target: "#blog" },
    { key: "report", path: "/report" },
  ];

  useEffect(() => {
    let isMounted = true;

    const fetchGaStats = async () => {
      try {
        const res = await publicClient.get("v2/statistic/ga-visitors");

        if (isMounted && res?.data) {
          setGaStats(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch GA stats:", err);
      }
    };

    fetchGaStats();

    return () => {
      isMounted = false;
    };
  }, []);

  const normalizeCount = (value) =>
    typeof value === "number" ? value + 100 : 100;

  const normalizeCountryName = (country) =>
    country?.trim() ? country : "Unknown";

  const COUNTRY_CODE_MAP = {
    "New Zealand": "NZ",
    Australia: "AU",
    Vietnam: "VN",
    "United States": "US",
    "United Kingdom": "GB",
    Canada: "CA",
    China: "CN",
    Japan: "JP",
    "South Korea": "KR",
    Thailand: "TH",
    Singapore: "SG",
    Malaysia: "MY",
    Indonesia: "ID",
    Philippines: "PH",
    India: "IN",
    France: "FR",
    Germany: "DE",
    Netherlands: "NL",
    Spain: "ES",
    Italy: "IT",
    Brazil: "BR",
    Mexico: "MX",
    "South Africa": "ZA",
  };

  const isoToFlag = (iso) =>
    iso?.length === 2
      ? iso
          .toUpperCase()
          .replace(/./g, (char) =>
            String.fromCodePoint(127397 + char.charCodeAt()),
          )
      : "🌐";

  const countryToFlag = (country) => {
    const iso =
      COUNTRY_CODE_MAP[country] || country?.slice(0, 2)?.toUpperCase();

    return isoToFlag(iso);
  };

  const scrollToSection = (target) => {
    if (location.pathname !== "/") {
      navigate(`/${target}`);

      // Wait until the homepage has rendered
      setTimeout(() => {
        document.querySelector(target)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);

      return;
    }

    document.querySelector(target)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleNavItem = (item) => {
    if (item.path) {
      navigate(item.path);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    scrollToSection(item.target);
  };

  const goWebsite = () => {
    window.open(
      "https://cdanz.org.nz/PersonProfile?Action=View&PersonProfile_id=638",
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <footer className="bg-gray-900 px-6 py-12 text-gray-400">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ amount: 0.1, once: true }}
        className="mx-auto max-w-5xl text-center"
      >
        {/* Membership */}
        <div className="flex flex-col items-center justify-center gap-2">
          <span className="text-sm">Member of</span>

          <button
            type="button"
            onClick={goWebsite}
            aria-label="View CDANZ member profile"
            className="transition hover:opacity-80"
          >
            <img
              src="/images/cdanz.png"
              alt="Career Development Association of New Zealand"
              width={300}
            />
          </button>
        </div>

        {/* Footer navigation */}
        <nav
          aria-label="Footer navigation"
          className="mt-10 border-y border-white/10 py-8"
        >
          <div
            className="
      grid grid-cols-1 gap-10
      md:grid-cols-[minmax(220px,1fr)_auto]
      md:items-start md:gap-16
    "
          >
            {/* Brand */}
            <div className="flex flex-col items-center md:items-start">
              <button
                type="button"
                onClick={() => handleNavItem(footerNavItems[0])}
                aria-label={navT.home}
                className="
          rounded-md transition duration-300
          hover:opacity-80
          focus:outline-none focus:ring-2
          focus:ring-neonYellow/70 focus:ring-offset-4
          focus:ring-offset-gray-900
        "
              >
                <img
                  src="/images/logo_black.png"
                  alt="Tiffany Bui"
                  className="h-auto w-32 object-contain md:w-36"
                />
              </button>
            </div>

            {/* Navigation columns */}
            <div
              className="
    grid w-full grid-cols-2 gap-x-8
    text-center
    sm:gap-x-14
    md:w-auto md:gap-x-20 md:text-left
  "
            >
              {/* Explore */}
              <div className="flex flex-col items-center md:items-start">
                <h3
                  className="
        mb-5 text-xs font-semibold uppercase
        tracking-[0.18em] text-gray-500
      "
                >
                  {lang === "vi" ? "Khám phá" : "Explore"}
                </h3>

                <div className="flex flex-col items-center gap-4 md:items-start">
                  {footerNavItems.slice(0, 4).map((item) => {
                    const isActive =
                      item.path && location.pathname === item.path;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleNavItem(item)}
                        className={`
              group relative whitespace-nowrap text-center
              text-sm font-medium transition duration-200
              hover:text-neonYellow
              md:text-left md:hover:translate-x-1
              ${isActive ? "text-neonYellow" : "text-gray-300"}
            `}
                      >
                        <span
                          className="
                absolute -left-3 top-1/2 hidden h-1 w-1
                -translate-y-1/2 rounded-full bg-neonYellow
                opacity-0 transition
                group-hover:opacity-100 md:block
              "
                        />

                        {navT[item.key]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Resources */}
              <div className="flex flex-col items-center md:items-start">
                <h3
                  className="
        mb-5 text-xs font-semibold uppercase
        tracking-[0.18em] text-gray-500
      "
                >
                  {lang === "vi" ? "Tài nguyên" : "Resources"}
                </h3>

                <div className="flex flex-col items-center gap-4 md:items-start">
                  {footerNavItems.slice(4).map((item) => {
                    const isActive =
                      item.path && location.pathname === item.path;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleNavItem(item)}
                        className={`
              group relative whitespace-nowrap text-center
              text-sm font-medium transition duration-200
              hover:text-neonYellow
              md:text-left md:hover:translate-x-1
              ${isActive ? "text-neonYellow" : "text-gray-300"}
            `}
                      >
                        <span
                          className="
                absolute -left-3 top-1/2 hidden h-1 w-1
                -translate-y-1/2 rounded-full bg-neonYellow
                opacity-0 transition
                group-hover:opacity-100 md:block
              "
                        />

                        {navT[item.key]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Footer bottom */}
        <div className="mt-6">
          <div className="flex flex-col items-center justify-between gap-4 text-xs text-gray-500 md:flex-row">
            {/* Copyright */}
            <p>
              © {new Date().getFullYear()} Tiffany Bui. {t.copyright}
            </p>

            {/* Visitor statistics */}
            {gaStats && (
              <div className="flex flex-col items-center gap-2">
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <div className="flex items-center gap-1">
                    <Eye size={14} />

                    <span className="text-neonYellow">
                      {normalizeCount(gaStats?.realtime?.totalUsers)}
                    </span>

                    <span>{t.online}</span>
                  </div>

                  <span>•</span>

                  <div className="flex items-center gap-1">
                    <Globe size={14} />

                    <span className="text-neonYellow">
                      {normalizeCount(gaStats?.allTime?.totalUsers)}
                    </span>

                    <span>{t.visitors}</span>
                  </div>

                  {gaStats?.allTime?.byCountry?.length > 0 && (
                    <>
                      <span>•</span>

                      <div className="text-[11px]">
                        {gaStats.allTime.byCountry
                          .slice(0, 3)
                          .map((countryData) => {
                            const country = normalizeCountryName(
                              countryData.country,
                            );

                            return `${countryToFlag(
                              country,
                            )} ${normalizeCount(countryData.users)}`;
                          })
                          .join(" · ")}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Credit */}
            <p>
              {t.designBy}{" "}
              <a
                href="https://miraelite.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neonYellow hover:underline"
              >
                Mira Elite
              </a>{" "}
              {t.withLove}
            </p>
          </div>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
